import PropTypes from 'prop-types';
import * as React from 'react';
import { useRef, useCallback } from 'react';
import { getCoreProps } from '../core/core.utils.js';
import { TextInput } from '../text-input/text-input.component.js';
import { Button } from '../button/button.component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { RenderComp } from '../core/render-comp.component.js';
import { Icon } from '../icon/icon.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
const searchInputPropTypes = {
    /**
   * The visual variant.
   * Note that error is implied if the SearchInput is used inside of a FormInput that also contains a FormError.
   * Available options: 'standard', 'error'
   */ skin: PropTypes.oneOf([
        'standard',
        'error'
    ]),
    /**
   * The click handler for the clear button
   */ onClear: PropTypes.func,
    /**
   * The click handler for the search button
   */ onSearchSubmit: PropTypes.func,
    /**
   * The aria-label for the clear button, required
   */ accessibleTextForClearButton: PropTypes.string.isRequired,
    /**
   * The aria-label for the search button, required
   */ accessibleTextForSearchButton: PropTypes.string.isRequired
};
const propKeysToRemove = Object.keys(searchInputPropTypes);
const SearchInput = renderWithRef('SearchInput', searchInputPropTypes, (props, ref)=>{
    const innerRef = useRef();
    const combinedRef = assignRefs(ref, innerRef);
    const { disabled, onClear, onSearchSubmit, accessibleTextForClearButton, accessibleTextForSearchButton } = props;
    const containerProps = getCoreProps(props);
    const containerPropKeys = Object.keys(containerProps);
    // TODO (browser): we need to set the disabled attribute on the container for browser's that do not support :has
    containerProps.disabled = disabled;
    const onClearClick = useCallback((e)=>{
        if (innerRef.current) {
            innerRef.current.value = '';
        }
        if (onClear) {
            onClear(e);
        }
    }, [
        onClear
    ]);
    const classNames = new Set([
        'swan-search-input'
    ]);
    if (props.skin === 'error') classNames.add('swan-search-input-skin-error');
    return /*#__PURE__*/ React.createElement(RenderComp, {
        root: "div",
        classNames: classNames,
        props: containerProps
    }, /*#__PURE__*/ React.createElement(RenderComp, {
        forwardedRef: combinedRef,
        root: TextInput,
        props: _object_spread({
            type: 'search'
        }, props),
        propKeysToRemove: [
            ...containerPropKeys,
            ...propKeysToRemove
        ]
    }), /*#__PURE__*/ React.createElement(Button, {
        className: "swan-search-input-clear",
        onClick: onClearClick,
        "aria-label": accessibleTextForClearButton,
        disabled: disabled
    }, /*#__PURE__*/ React.createElement(Icon, {
        iconType: "closeSmall"
    })), /*#__PURE__*/ React.createElement("span", {
        className: "swan-search-divider"
    }), /*#__PURE__*/ React.createElement(Button, {
        type: "submit",
        className: "swan-search-input-submit",
        onClick: onSearchSubmit !== null && onSearchSubmit !== void 0 ? onSearchSubmit : undefined,
        "aria-label": accessibleTextForSearchButton,
        disabled: disabled
    }, /*#__PURE__*/ React.createElement(Icon, {
        iconType: "search"
    })));
});

export { SearchInput, searchInputPropTypes };
